import { useState, useCallback, useEffect, useMemo } from 'react';
// @mui
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
import { RouterLink } from 'src/routes/components';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// types
import { HTBaiViet } from 'src/types/htblog';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import {
  useTable,
  emptyRows,
  TableNoData,
  getComparator,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';
// sections
import DarkModeButton from 'src/components/button/button-dark-model';
import BlogTableRow from '../blog-table-row';
import BlogTableToolbar from '../blog-table-toolbar';
// hooks (local)
import useBlog from '../hooks/use-blog';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'title', label: 'Tiêu đề', width: 400 },
  { id: 'author', label: 'Tác giả', width: 180 },
  { id: 'category', label: 'Thể loại', width: 120 },
  { id: 'status', label: 'Trạng thái', width: 100, align: 'center' },
  { id: '', width: 88 },
];

const defaultFilters = {
  name: '',
  status: 'all',
  category: 'all',
};

export default function BlogListView() {
  const table = useTable();
  const settings = useSettingsContext();
  const router = useRouter();

  const [tableData, setTableData] = useState<HTBaiViet[]>([]);
  const [filters, setFilters] = useState(defaultFilters);

  // [Update] Lấy thêm categories và getCategories từ hook useBlog
  const { 
    blogs, 
    categories, // Danh sách thể loại từ API
    getBlogs, 
    getCategories, // Hàm gọi API thể loại
    deleteBlog, 
    loading 
  } = useBlog();

  // [Update] Gọi cả API bài viết và API thể loại
  const fetchData = useCallback(async () => {
    try {
      await Promise.all([
        getBlogs(),
        getCategories()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [getBlogs, getCategories]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (blogs && blogs.length > 0) {
      setTableData(blogs);
    }
  }, [blogs]);

  // [Update] Chuyển đổi dữ liệu categories từ API sang format options cho Toolbar
  // Lưu ý: Đang giả định object category có trường 'ten' hoặc 'name'. Bạn hãy chỉnh sửa field này khớp với model HTBaiVietTheLoai
  const categoryOptions = useMemo(() => {
    if (!categories) return [];
    
    return categories.map((cat: any) => ({
      // Sử dụng tên thể loại làm value để filter client-side dễ hơn với danh sách bài viết hiện tại
      // Nếu muốn filter theo ID, đổi value: cat.id (nhưng cần update logic applyFilter)
      value: cat.ten || cat.tenTheLoai || cat.name, 
      label: cat.ten || cat.tenTheLoai || cat.name,
    }));
  }, [categories]);

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(table.order, table.orderBy),
    filters,
  });

  const dataInPage = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );

  const denseHeight = table.dense ? 52 : 72;

  const canReset = !!filters.name || filters.status !== 'all' || filters.category !== 'all';

  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

  const handleFilters = useCallback(
    (name: string, value: string) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleDeleteRow = useCallback(
    (id: number) => {
      const deleteRow = tableData.filter((row) => row.id !== id);
      setTableData(deleteRow);
      table.onUpdatePageDeleteRow(dataInPage.length);
    },
    [dataInPage.length, table, tableData]
  );

  const handleDeleteRows = useCallback(async () => {
    try {
      const selectedIds = table.selected.map(id => Number(id));
      const success = await deleteBlog(selectedIds);

      if (success) {
        const deleteRows = tableData.filter((row) => !table.selected.includes(String(row.id)));
        setTableData(deleteRows);
        table.onUpdatePageDeleteRows({
          totalRows: tableData.length,
          totalRowsInPage: dataInPage.length,
          totalRowsFiltered: dataFiltered.length,
        });
      }
    } catch (error) {
      console.error('Error deleting blogs:', error);
    }
  }, [deleteBlog, table, tableData, dataInPage.length, dataFiltered.length]);

  const handleEditRow = useCallback(
    (id: number) => {
      router.push(paths.dashboard.blog.edit(id));
    },
    [router]
  );

  const handleViewRow = useCallback(
    (id: number) => {
      router.push(paths.dashboard.blog.details(id));
    },
    [router]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Quản lý Blog"
        links={[
          { name: 'Dashboard', href: paths.dashboard.root },
          { name: 'Blog', href: paths.dashboard.blog.root },
          { name: 'Danh sách' },
        ]}
        action={
          <DarkModeButton
            component={RouterLink}
            href={paths.dashboard.blog.new}
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            Thêm bài viết mới
          </DarkModeButton>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
          <CircularProgress />  
        </Box>
      ) : (
      <Card>
        <BlogTableToolbar
          filters={filters}
          onFilters={handleFilters}
          categoryOptions={categoryOptions} // Truyền options lấy từ API
          onResetFilters={handleResetFilters}
          canReset={canReset}
          numSelected={table.selected.length}
          onDeleteRows={handleDeleteRows}
          dataCounts={{
            total: tableData.length,
            public: tableData.filter(blog => blog.trangThai === 1).length,
            private: tableData.filter(blog => blog.trangThai !== 1).length,
          }}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={table.order}
                orderBy={table.orderBy}
                headLabel={TABLE_HEAD}
                rowCount={tableData.length}
                numSelected={table.selected.length}
                onSort={table.onSort}
                onSelectAllRows={(checked) =>
                  table.onSelectAllRows(
                    checked,
                    tableData.map((row) => String(row.id))
                  )
                }
              />

              <TableBody>
                {dataInPage.map((row) => (
                  <BlogTableRow
                    key={row.id}
                    row={row}
                    selected={table.selected.includes(String(row.id))}
                    onSelectRow={() => table.onSelectRow(String(row.id))}
                    onDeleteRow={() => handleDeleteRow(row.id)}
                    onEditRow={() => handleEditRow(row.id)}
                    onViewRow={() => handleViewRow(row.id)}
                  />
                ))}

                <TableEmptyRows
                  height={denseHeight}
                  emptyRows={emptyRows(table.page, table.rowsPerPage, tableData.length)}
                />

                <TableNoData notFound={notFound} content='Không tìm thấy dữ liệu' />
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={dataFiltered.length}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
      )}

    </Container>
  );
}

// ----------------------------------------------------------------------


function applyFilter({
  inputData,
  comparator,
  filters,
}: {
  inputData: HTBaiViet[];
  comparator: (a: any, b: any) => number;
  filters: { name: string; status: string; category: string };
}) {
  const { name, status, category } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (name) {
    inputData = inputData.filter((blog) =>
      blog.tieuDe.toLowerCase().includes(name.toLowerCase()) ||
      blog.moTaNgan.toLowerCase().includes(name.toLowerCase())
    );
  }

  if (status !== 'all') {
    inputData = inputData.filter((blog) => {
      if (status === 'public') return blog.trangThai === 1;
      if (status === 'private') return blog.trangThai !== 1;
      return true;
    });
  }

  if (category !== 'all') {
    inputData = inputData.filter((blog: any) => {
       const categoryName = 
          blog.htBaiVietTheLoai?.ten ||       
         
          (typeof blog.category === 'string' ? blog.category : ''); 
      
       return categoryName === category;
    });
  }

  return inputData;
}